import React, { useState, useEffect } from 'react';
import { Store, Plus } from 'lucide-react';
import { useOrganization } from '../../hooks/useOrganization';
import { canEdit } from '../../utils/permissions';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

const AdvertiserSelector = ({
  value,
  onChange,
  placeholder = 'Select advertiser',
  allowCreate = true,
  style = {},
  size = 'middle'
}) => {
  const { advertisers, loadAdvertisers, createAdvertiser, currentRole, isLoading } = useOrganization();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', industry: '', website: '' });

  useEffect(() => {
    loadAdvertisers();
  }, [loadAdvertisers]);

  const handleCreateAdvertiser = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setCreating(true);
    try {
      const newAdvertiser = await createAdvertiser(formData);
      if (newAdvertiser) {
        toast.success(`Advertiser "${newAdvertiser.name}" created`);
        onChange?.(newAdvertiser.id);
        setCreateModalOpen(false);
        setFormData({ name: '', industry: '', website: '' });
      }
    } catch (error) {
      toast.error('Failed to create advertiser');
    } finally {
      setCreating(false);
    }
  };

  const canCreateAdvertiser = allowCreate && canEdit(currentRole);
  const selectedAdvertiser = advertisers.find(a => a.id === value);

  return (
    <>
      <div className="flex gap-2 w-full" style={style}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn(
                "flex-1 justify-between font-normal",
                !value && "text-muted-foreground"
              )}
            >
              {selectedAdvertiser ? (
                <span className="flex items-center gap-2">
                  <Store className="h-4 w-4 text-muted-foreground" />
                  {selectedAdvertiser.name}
                </span>
              ) : (
                placeholder
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search advertisers..." />
              <CommandList>
                <CommandEmpty>
                  {isLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Spinner size="sm" />
                    </div>
                  ) : (
                    'No advertisers found.'
                  )}
                </CommandEmpty>
                <CommandGroup>
                  {advertisers.map((adv) => (
                    <CommandItem
                      key={adv.id}
                      value={adv.name}
                      onSelect={() => {
                        onChange?.(adv.id);
                        setOpen(false);
                      }}
                    >
                      <Store className="h-4 w-4 mr-2 text-muted-foreground" />
                      {adv.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
                {canCreateAdvertiser && (
                  <>
                    <CommandSeparator />
                    <CommandGroup>
                      <CommandItem
                        onSelect={() => {
                          setOpen(false);
                          setCreateModalOpen(true);
                        }}
                        className="text-coral"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create New Advertiser
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <Dialog open={createModalOpen} onOpenChange={(v) => {
        setCreateModalOpen(v);
        if (!v) setFormData({ name: '', industry: '', website: '' });
      }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Create New Advertiser</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAdvertiser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="adv-name">Advertiser Name *</Label>
              <Input
                id="adv-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Nike, Coca-Cola, Local Restaurant"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="adv-industry">Industry (Optional)</Label>
              <Select
                value={formData.industry}
                onValueChange={(v) => setFormData(prev => ({ ...prev, industry: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="food_beverage">Food & Beverage</SelectItem>
                  <SelectItem value="technology">Technology</SelectItem>
                  <SelectItem value="healthcare">Healthcare</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="automotive">Automotive</SelectItem>
                  <SelectItem value="entertainment">Entertainment</SelectItem>
                  <SelectItem value="travel">Travel & Hospitality</SelectItem>
                  <SelectItem value="real_estate">Real Estate</SelectItem>
                  <SelectItem value="education">Education</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adv-website">Website (Optional)</Label>
              <Input
                id="adv-website"
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://example.com"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCreateModalOpen(false);
                  setFormData({ name: '', industry: '', website: '' });
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? <Spinner size="sm" className="mr-2" /> : null}
                Create Advertiser
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdvertiserSelector;
